import { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { NMObjectHeaderReport } from "@neumetric/blueprint-lib/flows";
import {
  Col,
  NMWaitIndicator,
  NMPDFViewer,
  NMPopup,
} from "@neumetric/blueprint-lib/components";
import { getCurrentUser, getModuleById } from "@neumetric/blueprint-lib/store";
import { DateTimeUtil, NMCrypto } from "@neumetric/blueprint-lib/utility";
import { AuditReport } from "../Common";
import { auditActions, getCurrentReportDetails } from "../../store";

export const ReportViewerScreen = () => {
  const _ref = useRef(null);
  const { reportId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const _reportRef = useRef();
  const currentUser = useSelector(getCurrentUser);
  const remediatorMod = useSelector(getModuleById.bind(null, "remediator"));
  const report = useSelector(getCurrentReportDetails);
  const [zoom, setZoom] = useState("page-width");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isExporting, setIsExporting] = useState("");

  useEffect(() => {
    if (!reportId) return;
    dispatch(auditActions.setCurrentReportDetails(null));
    dispatch(auditActions.fetchReportShow({ report_id: reportId }));
  }, [dispatch, reportId]);

  const isSoc2Type2Report = useMemo(() => {
    return report && report.report_format === "SOC 2 Type 2";
  }, [report]);

  const handleGoBack = () => navigate("/A/R");

  const handleZoomChange = (val) => setZoom(val);

  const showPdf = Boolean(report?.blob);

  const shareReport = (fileContent, emails) => {
    if (emails) {
      setIsSubmitting(true);
      const action = auditActions.shareAuditReport({
        reportId: reportId,
        emailAddresses: emails,
        fileContent: fileContent,
      });
      action.onComplete = () => {
        if (_ref.current && _ref.current.resetMenu) {
          _ref.current.resetMenu();
        }
        setIsSubmitting(false);
        setIsExporting("");
      };
      dispatch(action);
    } else {
      saveAs(fileContent, report.title);
    }
  };

  const hanleSoc2TypeExport = async (emails) => {
    setIsExporting(
      `${emails ? "Sharing" : "Exporting"} Report. Please wait...`,
    );
    const exportData = await _reportRef.current.getContentForExport();
    if (emails) {
      shareReport(exportData, emails);
    } else {
      const onComplete = (blob) => {
        setIsExporting("");
        saveAs(blob, report.title);
      };
      exportData.id = report.id;
      exportData.report_id = reportId;
      const action = auditActions.generateSoc2Type2Report(exportData);
      action.onComplete = onComplete;
      dispatch(action);
    }
  };

  const handleExport = (_, emails) => {
    if (isSoc2Type2Report) {
      hanleSoc2TypeExport(emails);
    } else {
      shareReport(report.blob, emails);
    }
  };

  const handleOnSaveComplete = () => {
    setIsSubmitting(false);
    setIsEdit(false);
  };
  const getSoc2Type2Headers = () => {
    const addProps = {};
    if (isSoc2Type2Report && report) {
      if (_reportRef.current) {
        if (!isEdit) {
          addProps.edit = {
            onClick: () => {
              setIsEdit(true);
              _reportRef.current?.onEdit();
            },
          };
        }
        if (isEdit) {
          addProps.save = {
            onClick: () => {
              setIsSubmitting(true);
              _reportRef.current?.onSave();
            },
          };
          addProps.cancel = {
            onClick: () => {
              setIsEdit(false);
              _reportRef.current?.onCancel();
            },
          };
        }
      }
    }
    return addProps;
  };

  const handleMenuClick = (id) => {
    if (id === "view_task") {
      const redirectData = {
        path: "/T",
      };
      const filter = {
        type: {
          label: "Audit",
          id: "audit",
          fetchKey: "audit_id",
          queryKey: "audit",
          key: "audit",
          value: "Audit",
        },
        search: {
          label: report.title,
          id: report.id,
          guid: report.guid,
        },
      };
      const searchParams = new URLSearchParams();
      searchParams.set("page", "1");
      searchParams.set("filter", encodeURIComponent(JSON.stringify(filter)));
      redirectData.search = `?${searchParams.toString()}`;
      let redirect = NMCrypto.encrypt(redirectData);
      const path = `${remediatorMod.link}login?sessionToken=${currentUser.sessionToken}&redirect=${redirect}`;
      window.open(path, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Col className="w-100 h-100 overflow-hide bg-contrast report-view">
      {!report && (
        <Col className="w-100 h-100" align="center" justify="center">
          <NMWaitIndicator size="small" />
        </Col>
      )}
      {report && (
        <Fragment>
          <NMObjectHeaderReport
            ref={_ref}
            object={report}
            classes={{ root: "no-shadow", title: "text-heading" }}
            objectProps={{
              name: "Report",
              idAttribute: "report_id",
              titleAttribute: "title",
            }}
            level={{ label: "Back", onClick: handleGoBack }}
            formData={{
              ...report,
              // optional display formatting
              reportdate_label: report?.reportdate
                ? new DateTimeUtil(report.reportdate).toString(
                    "ddd, DD-MMM-YYYY",
                  )
                : "",
            }}
            zoom={{
              options: [
                { label: "Fit Width", id: "page-width" },
                { label: "Fit Page", id: "page-fit" },
                { label: "100%", id: 1 },
                { label: "125%", id: 1.25 },
                { label: "150%", id: 1.5 },
                { label: "200%", id: 2 },
              ],
              defaultValue: "page-width",
              onChange: handleZoomChange,
            }}
            menu={{
              onMenuClick: handleMenuClick,
              items: [
                {
                  id: "view_task",
                  label: "View Tasks",
                  icon: "EYE_OPEN",
                  variant: "CONTRAST",
                },
              ],
            }}
            exports={{
              showEncryption: false,
              filename: `${report?.title || "Report"}.pdf`,
              onExport: handleExport,
              allowedDomains: currentUser.org.email_domain.split(","),
              submitting: isSubmitting,
            }}
            isSubmitting={isSubmitting}
            {...getSoc2Type2Headers()}
          />

          <Col className="mb-16 mt-1 w-100 flex-auto overflow-auto report-view">
            {showPdf ? (
              <NMPDFViewer blob={report.blob} zoom={zoom} />
            ) : isSoc2Type2Report ? (
              <AuditReport
                ref={_reportRef}
                asmtId={report.asmt_id}
                allowEdit={true}
                onSaveComplete={handleOnSaveComplete}
              />
            ) : (
              <Col className="w-100 h-100" align="center" justify="center">
                <div className="text-placeholder">No PDF available</div>
              </Col>
            )}
          </Col>
        </Fragment>
      )}
      {isExporting !== "" && (
        <NMPopup visible>
          <NMWaitIndicator message={isExporting} />
        </NMPopup>
      )}
    </Col>
  );
};
